import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { getProfilePicUrl, getLocalProfilePicUrl } from '../../config/imageUrls';
import {
  Calendar, ChevronLeft, ChevronRight, Filter,
  Smartphone, Heart, UserPlus, UserMinus, Eye, MessageCircle, Star,
  Loader, RefreshCw, Clock
} from 'lucide-react';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--bg);
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DateInput = styled.input`
  padding: 7px 12px;
  background: var(--surface);
  border: 1px solid var(--card-border);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
  &:focus { outline: none; border-color: var(--primary); }
  &::-webkit-calendar-picker-indicator { filter: invert(0.7); cursor: pointer; }
`;

const ToolBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 12px;
  background: ${p => p.$active ? 'var(--primary)' : 'var(--surface)'};
  color: ${p => p.$active ? '#fff' : 'var(--text-secondary)'};
  border: 1px solid ${p => p.$active ? 'var(--primary)' : 'var(--card-border)'};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s;
  &:hover { border-color: var(--primary); color: ${p => p.$active ? '#fff' : 'var(--text-primary)'}; }
`;

const Separator = styled.div`
  width: 1px;
  height: 24px;
  background: var(--card-border);
  margin: 0 4px;
`;

const Badge = styled.span`
  padding: 3px 10px;
  background: rgba(var(--primary-rgb, 165, 145, 239), .15);
  color: var(--primary);
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
`;

const TimelineArea = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

/* ─── Left labels panel ─── */
const LabelsPanel = styled.div`
  flex-shrink: 0;
  width: 220px;
  border-right: 1px solid var(--card-border);
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--card-bg);
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--card-border); border-radius: 2px; }
`;

const LabelHeader = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-muted);
  border-bottom: 1px solid var(--card-border);
  background: var(--surface);
  position: sticky;
  top: 0;
  z-index: 4;
`;

const FilterBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'var(--text-muted)'};
  border: 1px solid ${p => p.$active ? 'var(--primary)' : 'var(--card-border)'};
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 9px;
  font-weight: 700;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: .5px;
  &:hover { border-color: var(--primary); color: ${p => p.$active ? '#fff' : 'var(--primary)'}; }
`;

const FilterDropdown = styled.div`
  position: absolute;
  top: 36px;
  left: 0;
  right: 0;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 0 0 8px 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,.3);
  z-index: 10;
  max-height: 320px;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--card-border); border-radius: 2px; }
`;

const FilterSection = styled.div`
  border-bottom: 1px solid var(--card-border);
  &:last-child { border-bottom: none; }
`;

const FilterSectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-primary);
  cursor: pointer;
  background: var(--surface);
  &:hover { background: rgba(var(--primary-rgb, 165, 145, 239), .08); }
`;

const FilterItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 24px;
  font-size: 11px;
  color: var(--text-secondary);
  cursor: pointer;
  &:hover { background: rgba(var(--primary-rgb, 165, 145, 239), .06); color: var(--text-primary); }
  input { accent-color: var(--primary); cursor: pointer; }
`;

const WorkflowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: ${p => p.$height}px;
  background: rgba(var(--primary-rgb, 165, 145, 239), .06);
  border-bottom: 1px solid var(--card-border);
  cursor: pointer;
  &:hover { background: rgba(var(--primary-rgb, 165, 145, 239), .12); }
`;

const WfLetter = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${p => p.$color};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 14px;
  flex-shrink: 0;
`;

const WfName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeviceLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px 0 28px;
  height: ${p => p.$height}px;
  border-bottom: 1px solid var(--card-border);
`;

const DeviceAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const DeviceText = styled.div`
  flex: 1;
  min-width: 0;
`;

const DeviceUsername = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeviceSubtext = styled.div`
  font-size: 10px;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ─── Right timeline grid ─── */
const GridPanel = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--card-border); border-radius: 4px; }
`;

const GridInner = styled.div`
  position: relative;
  width: 100%;
`;

const TimeHeader = styled.div`
  height: 36px;
  position: sticky;
  top: 0;
  z-index: 3;
  background: var(--surface);
  border-bottom: 1px solid var(--card-border);
  width: 100%;
`;

const TimeTick = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: var(--text-muted);
  border-left: 1px solid var(--card-border);
  padding-left: 6px;
  white-space: nowrap;
`;

const GridRow = styled.div`
  position: relative;
  height: ${p => p.$height}px;
  border-bottom: 1px solid var(--card-border);
  background: ${p => p.$isWorkflow ? 'rgba(var(--primary-rgb, 165, 145, 239), .03)' : 'transparent'};
  &:hover { background: rgba(var(--primary-rgb, 165, 145, 239), .05); }
`;

const GridLine = styled.div`
  position: absolute;
  top: 0;
  width: 1px;
  height: 100%;
  background: var(--card-border);
  opacity: .4;
`;

const SessionBlock = styled.div`
  position: absolute;
  top: 4px;
  height: calc(100% - 8px);
  border-radius: 4px;
  cursor: pointer;
  transition: filter .15s, transform .15s;
  display: flex;
  align-items: center;
  overflow: hidden;
  min-width: 2px;
  &:hover {
    filter: brightness(1.2);
    transform: scaleY(1.15);
    z-index: 5;
  }
`;

const SessionInner = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 6px;
  white-space: nowrap;
  font-size: 10px;
  font-weight: 700;
  color: rgba(255,255,255,.9);
  overflow: hidden;
`;

/* ─── Tooltip ─── */
const Tooltip = styled.div`
  position: fixed;
  z-index: 100;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 10px;
  padding: 12px 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,.3);
  pointer-events: none;
  min-width: 200px;
  max-width: 280px;
`;

const TtTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TtRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--text-secondary);
  margin-bottom: 3px;
  svg { flex-shrink: 0; }
`;

const TtValue = styled.span`
  font-weight: 700;
  color: var(--text-primary);
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--text-muted);
  font-size: 14px;
  svg { opacity: .3; }
`;

const LoadingState = styled(EmptyState)`
  svg { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

/* ═══════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════ */

const DEVICE_ROW_H = 36;
const MAX_SESSION_DURATION = 4 * 3600; // 4h cap for sessions without end_time

const WORKFLOW_COLORS = [
  '#6366f1', '#ec4899', '#f59e0b', '#22c55e', '#06b6d4',
  '#8b5cf6', '#ef4444', '#14b8a6', '#f97316', '#3b82f6'
];

const SESSION_TYPE_COLORS = {
  interact: '#6366f1',
  scrape: '#06b6d4',
  unfollow: '#ef4444',
  dm: '#f59e0b',
  'follower-analysis': '#22c55e',
  unknown: '#8b5cf6',
};

const getSessionColor = (type) => SESSION_TYPE_COLORS[type] || SESSION_TYPE_COLORS.unknown;

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */
export default function WorkflowSessionTimeline() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 3);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [activePreset, setActivePreset] = useState('3j');
  const [tooltip, setTooltip] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [hiddenDevices, setHiddenDevices] = useState(new Set());
  const [hiddenWorkflows, setHiddenWorkflows] = useState(new Set());

  const gridRef = useRef(null);
  const labelsRef = useRef(null);
  const gridPanelRef = useRef(null);
  const [gridPanelWidth, setGridPanelWidth] = useState(800);

  // ─── Measure grid panel width for fit-to-view ───
  useEffect(() => {
    const el = gridPanelRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        setGridPanelWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ─── Fetch data ───
  const fetchTimeline = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/workflow-builder/timeline', {
        params: { start_date: startDate, end_date: endDate }
      });
      setWorkflows(res.data.workflows || []);
    } catch (err) {
      console.error('Timeline fetch error:', err);
      setWorkflows([]);
    }
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  // ─── Fixed time range = exactly the selected date boundaries ───
  const { globalStart, globalEnd, totalHours } = useMemo(() => {
    const sd = new Date(startDate + 'T00:00:00');
    const ed = new Date(endDate + 'T23:59:59');
    return {
      globalStart: sd.getTime() / 1000,
      globalEnd: ed.getTime() / 1000,
      totalHours: (ed.getTime() - sd.getTime()) / 3600000
    };
  }, [startDate, endDate]);

  // ─── Filtered workflows ───
  const filteredWorkflows = useMemo(() => {
    return workflows
      .filter(wf => !hiddenWorkflows.has(wf.letter))
      .map(wf => ({
        ...wf,
        devices: wf.devices.filter(dev => !hiddenDevices.has(dev.device_id))
      }))
      .filter(wf => wf.devices.length > 0);
  }, [workflows, hiddenWorkflows, hiddenDevices]);

  // ─── Build rows ───
  const rows = useMemo(() => {
    const out = [];
    filteredWorkflows.forEach((wf, wfIdx) => {
      const globalIdx = workflows.findIndex(w => w.letter === wf.letter);
      const colorIdx = globalIdx >= 0 ? globalIdx : wfIdx;
      const isCollapsed = collapsed[wf.letter];
      out.push({
        type: 'workflow',
        letter: wf.letter,
        color: WORKFLOW_COLORS[colorIdx % WORKFLOW_COLORS.length],
        deviceCount: wf.devices.length,
        sessionCount: wf.devices.reduce((s, d) => s + d.sessions.length, 0),
        height: DEVICE_ROW_H,
        isCollapsed
      });
      if (!isCollapsed) {
        wf.devices.forEach(dev => {
          out.push({
            type: 'device',
            wfLetter: wf.letter,
            wfColor: WORKFLOW_COLORS[colorIdx % WORKFLOW_COLORS.length],
            device_id: dev.device_id,
            device_name: dev.device_name,
            instagram_username: dev.instagram_username,
            sessions: dev.sessions,
            height: DEVICE_ROW_H,
          });
        });
      }
    });
    return out;
  }, [filteredWorkflows, workflows, collapsed]);

  // ─── Sync scroll between labels and grid ───
  useEffect(() => {
    const grid = gridRef.current;
    const labels = labelsRef.current;
    if (!grid || !labels) return;
    const onScroll = () => { labels.scrollTop = grid.scrollTop; };
    grid.addEventListener('scroll', onScroll);
    return () => grid.removeEventListener('scroll', onScroll);
  }, []);

  // ─── Time ticks — proportional to period ───
  const ticks = useMemo(() => {
    const out = [];
    const totalDays = totalHours / 24;

    // Choose tick interval proportional to period
    let intervalH;
    if (totalDays <= 1) intervalH = 1;
    else if (totalDays <= 3) intervalH = 3;
    else if (totalDays <= 7) intervalH = 6;
    else if (totalDays <= 14) intervalH = 12;
    else intervalH = 24;

    const startD = new Date(globalStart * 1000);
    const firstTick = new Date(startD);
    if (intervalH >= 24) {
      firstTick.setHours(0, 0, 0, 0);
    } else {
      const h = Math.ceil(startD.getHours() / intervalH) * intervalH;
      firstTick.setHours(h, 0, 0, 0);
    }

    let t = firstTick.getTime() / 1000;
    const stepSec = intervalH * 3600;
    while (t <= globalEnd) {
      const d = new Date(t * 1000);
      const pct = (t - globalStart) / (globalEnd - globalStart);
      const isDay = d.getHours() === 0 && d.getMinutes() === 0;
      let label;
      if (intervalH >= 24) {
        label = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
      } else if (isDay) {
        label = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
      } else {
        label = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      }
      out.push({ pct, label, isDay: isDay || intervalH >= 24 });
      t += stepSec;
    }
    return out;
  }, [globalStart, globalEnd, totalHours]);

  // ─── Date helpers ───
  const shiftDates = (days) => {
    const s = new Date(startDate);
    const e = new Date(endDate);
    s.setDate(s.getDate() + days);
    e.setDate(e.getDate() + days);
    setStartDate(s.toISOString().split('T')[0]);
    setEndDate(e.toISOString().split('T')[0]);
    setActivePreset(null);
  };

  const setPreset = (days, label) => {
    const e = new Date();
    const s = new Date();
    s.setDate(s.getDate() - (days - 1));
    setStartDate(s.toISOString().split('T')[0]);
    setEndDate(e.toISOString().split('T')[0]);
    setActivePreset(label);
  };

  const handleStartDateChange = (val) => { setStartDate(val); setActivePreset(null); };
  const handleEndDateChange = (val) => { setEndDate(val); setActivePreset(null); };

  // ─── Format duration ───
  const fmtDuration = (sec) => {
    if (!sec || sec <= 0) return '—';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return h > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${m}min`;
  };

  // ─── Toggle workflow collapse ───
  const toggleCollapse = (letter) => {
    setCollapsed(prev => ({ ...prev, [letter]: !prev[letter] }));
  };

  // ─── Session position (percentage-based for fit-to-view) ───
  const sessionLeft = (s) => {
    const pct = (s.start_time - globalStart) / (globalEnd - globalStart);
    return `${Math.max(0, pct * 100)}%`;
  };
  const sessionWidth = (s) => {
    // Cap sessions without end_time
    let endT = s.end_time;
    if (!endT || endT <= s.start_time) {
      endT = s.start_time + Math.min(Math.max(s.duration, 300), MAX_SESSION_DURATION);
    }
    // Also cap if end extends past globalEnd
    endT = Math.min(endT, globalEnd);
    const pct = (endT - s.start_time) / (globalEnd - globalStart);
    return `${Math.max(0.15, pct * 100)}%`;
  };

  // ─── Filter toggle helpers ───
  const toggleDevice = (deviceId) => {
    setHiddenDevices(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId); else next.add(deviceId);
      return next;
    });
  };

  const toggleWorkflow = (letter) => {
    setHiddenWorkflows(prev => {
      const next = new Set(prev);
      if (next.has(letter)) next.delete(letter); else next.add(letter);
      return next;
    });
  };

  const hasActiveFilters = hiddenDevices.size > 0 || hiddenWorkflows.size > 0;
  const clearFilters = () => { setHiddenDevices(new Set()); setHiddenWorkflows(new Set()); };

  // ─── Tooltip handlers ───
  const showTooltip = (e, session, deviceName, username) => {
    setTooltip({
      x: e.clientX + 12,
      y: e.clientY - 8,
      session,
      deviceName,
      username
    });
  };
  const hideTooltip = () => setTooltip(null);

  const totalSessions = filteredWorkflows.reduce((s, w) => s + w.devices.reduce((s2, d) => s2 + d.sessions.length, 0), 0);

  // ─── Render ───
  if (loading) {
    return (
      <Container>
        <LoadingState>
          <Loader size={32} />
          Chargement de la timeline...
        </LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      {/* ═══ TOOLBAR ═══ */}
      <Toolbar>
        <ToolGroup>
          <Calendar size={14} color="var(--primary)" />
          <DateInput type="date" value={startDate} onChange={e => handleStartDateChange(e.target.value)} />
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>→</span>
          <DateInput type="date" value={endDate} onChange={e => handleEndDateChange(e.target.value)} />
        </ToolGroup>

        <ToolGroup>
          <ToolBtn onClick={() => shiftDates(-1)}><ChevronLeft size={14} /></ToolBtn>
          <ToolBtn onClick={() => shiftDates(1)}><ChevronRight size={14} /></ToolBtn>
        </ToolGroup>

        <Separator />

        <ToolGroup>
          <ToolBtn $active={activePreset === '1j'} onClick={() => setPreset(1, '1j')}>Aujourd'hui</ToolBtn>
          <ToolBtn $active={activePreset === '3j'} onClick={() => setPreset(3, '3j')}>3j</ToolBtn>
          <ToolBtn $active={activePreset === '7j'} onClick={() => setPreset(7, '7j')}>7j</ToolBtn>
          <ToolBtn $active={activePreset === '14j'} onClick={() => setPreset(14, '14j')}>14j</ToolBtn>
          <ToolBtn $active={activePreset === '30j'} onClick={() => setPreset(30, '30j')}>30j</ToolBtn>
        </ToolGroup>

        <Separator />

        <ToolBtn onClick={fetchTimeline}><RefreshCw size={14} /> Actualiser</ToolBtn>
        <Badge>{totalSessions} session{totalSessions !== 1 ? 's' : ''}</Badge>
        {hasActiveFilters && (
          <ToolBtn $active onClick={clearFilters}>✕ Effacer filtres</ToolBtn>
        )}
      </Toolbar>

      {filteredWorkflows.length === 0 && !loading ? (
        <EmptyState>
          <Calendar size={40} />
          {hasActiveFilters ? 'Tous les éléments sont filtrés' : 'Aucune session sur cette période'}
        </EmptyState>
      ) : (
        <TimelineArea>
          {/* ═══ LEFT LABELS ═══ */}
          <LabelsPanel ref={labelsRef} style={{ position: 'relative' }}>
            <LabelHeader>
              <span>Workflows / Devices</span>
              <FilterBtn $active={showFilter || hasActiveFilters} onClick={() => setShowFilter(p => !p)}>
                <Filter size={10} /> Filtrer
              </FilterBtn>
            </LabelHeader>

            {/* ─── Filter dropdown ─── */}
            {showFilter && (
              <FilterDropdown>
                {workflows.map((wf, wfIdx) => {
                  const wfHidden = hiddenWorkflows.has(wf.letter);
                  return (
                    <FilterSection key={wf.letter}>
                      <FilterSectionHead onClick={() => toggleWorkflow(wf.letter)}>
                        <input
                          type="checkbox"
                          checked={!wfHidden}
                          readOnly
                          style={{ accentColor: WORKFLOW_COLORS[wfIdx % WORKFLOW_COLORS.length], cursor: 'pointer' }}
                        />
                        <WfLetter $color={WORKFLOW_COLORS[wfIdx % WORKFLOW_COLORS.length]} style={{ width: 20, height: 20, fontSize: 11 }}>
                          {wf.letter}
                        </WfLetter>
                        <span>Workflow {wf.letter}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-muted)' }}>
                          {wf.devices.length} dev.
                        </span>
                      </FilterSectionHead>
                      {!wfHidden && wf.devices.map(dev => (
                        <FilterItem key={dev.device_id}>
                          <input
                            type="checkbox"
                            checked={!hiddenDevices.has(dev.device_id)}
                            onChange={() => toggleDevice(dev.device_id)}
                          />
                          {dev.instagram_username && dev.instagram_username !== 'Unknown'
                            ? `@${dev.instagram_username}`
                            : dev.device_name}
                        </FilterItem>
                      ))}
                    </FilterSection>
                  );
                })}
              </FilterDropdown>
            )}

            {rows.map((row, i) => {
              if (row.type === 'workflow') {
                return (
                  <WorkflowLabel key={`wf-${row.letter}`} $height={row.height} onClick={() => toggleCollapse(row.letter)}>
                    <WfLetter $color={row.color}>{row.letter}</WfLetter>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <WfName>Workflow {row.letter}</WfName>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                        {row.deviceCount} device{row.deviceCount !== 1 ? 's' : ''} • {row.sessionCount} session{row.sessionCount !== 1 ? 's' : ''}
                        {row.isCollapsed ? ' ▸' : ' ▾'}
                      </div>
                    </div>
                  </WorkflowLabel>
                );
              }
              return (
                <DeviceLabel key={`dev-${row.device_id}-${i}`} $height={row.height}>
                  <DeviceAvatar>
                    {row.instagram_username && row.instagram_username !== 'Unknown' ? (
                      <img
                        src={getLocalProfilePicUrl(row.instagram_username)}
                        alt=""
                        onError={e => {
                          const step = parseInt(e.target.dataset.fb || '0');
                          if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(row.instagram_username); return; }
                          if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${row.instagram_username}`; return; }
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <Smartphone size={12} color="#fff" />
                    )}
                  </DeviceAvatar>
                  <DeviceText>
                    <DeviceUsername>
                      {row.instagram_username && row.instagram_username !== 'Unknown' ? `@${row.instagram_username}` : row.device_name}
                    </DeviceUsername>
                    <DeviceSubtext>{row.device_name}</DeviceSubtext>
                  </DeviceText>
                </DeviceLabel>
              );
            })}
          </LabelsPanel>

          {/* ═══ RIGHT GRID (fit-to-view, no horizontal scroll) ═══ */}
          <GridPanel ref={(el) => { gridRef.current = el; gridPanelRef.current = el; }}>
            <GridInner style={{ width: '100%', minHeight: rows.length * DEVICE_ROW_H + 36 }}>
              {/* Time header */}
              <TimeHeader>
                {ticks.map((tick, i) => (
                  <TimeTick key={i} style={{
                    left: `${tick.pct * 100}%`,
                    fontWeight: tick.isDay ? 800 : 600,
                    color: tick.isDay ? 'var(--primary)' : 'var(--text-muted)'
                  }}>
                    {tick.label}
                  </TimeTick>
                ))}
              </TimeHeader>

              {/* Rows */}
              {rows.map((row, rowIdx) => {
                const top = 36 + rowIdx * DEVICE_ROW_H;

                return (
                  <GridRow
                    key={`row-${rowIdx}`}
                    $height={row.height}
                    $isWorkflow={row.type === 'workflow'}
                    style={{ position: 'absolute', top, left: 0, width: '100%' }}
                  >
                    {/* Vertical grid lines */}
                    {ticks.map((tick, ti) => (
                      <GridLine key={ti} style={{ left: `${tick.pct * 100}%`, opacity: tick.isDay ? .6 : .2 }} />
                    ))}

                    {/* Session blocks */}
                    {row.type === 'device' && row.sessions.map(session => {
                      const color = getSessionColor(session.session_type);
                      const left = sessionLeft(session);
                      const width = sessionWidth(session);
                      // Compute pixel width for label visibility
                      let endT = session.end_time;
                      if (!endT || endT <= session.start_time) {
                        endT = session.start_time + Math.min(Math.max(session.duration, 300), MAX_SESSION_DURATION);
                      }
                      endT = Math.min(endT, globalEnd);
                      const pxW = ((endT - session.start_time) / (globalEnd - globalStart)) * gridPanelWidth;
                      const noEnd = !session.end_time || session.end_time <= session.start_time;

                      return (
                        <SessionBlock
                          key={session.id}
                          style={{
                            left,
                            width,
                            background: noEnd
                              ? `repeating-linear-gradient(135deg, ${color}, ${color} 4px, ${color}99 4px, ${color}99 8px)`
                              : `linear-gradient(135deg, ${color}, ${color}cc)`,
                            boxShadow: `0 2px 8px ${color}44`,
                            borderRight: noEnd ? `2px dashed ${color}` : 'none',
                          }}
                          onMouseEnter={e => showTooltip(e, session, row.device_name, row.instagram_username)}
                          onMouseMove={e => showTooltip(e, session, row.device_name, row.instagram_username)}
                          onMouseLeave={hideTooltip}
                        >
                          {pxW > 40 && (
                            <SessionInner>
                              {session.session_type === 'interact' && <Heart size={10} />}
                              {session.session_type === 'unfollow' && <UserMinus size={10} />}
                              {session.session_type === 'scrape' && <Eye size={10} />}
                              {pxW > 80 && fmtDuration(session.duration)}
                              {noEnd && pxW > 100 && <span style={{ opacity: .6, fontSize: 9 }}>(en cours?)</span>}
                            </SessionInner>
                          )}
                        </SessionBlock>
                      );
                    })}
                  </GridRow>
                );
              })}
            </GridInner>
          </GridPanel>
        </TimelineArea>
      )}

      {/* ═══ TOOLTIP ═══ */}
      {tooltip && (
        <Tooltip style={{ left: tooltip.x, top: tooltip.y }}>
          <TtTitle>
            <div style={{
              width: 10, height: 10, borderRadius: 3,
              background: getSessionColor(tooltip.session.session_type)
            }} />
            {tooltip.session.session_type || 'Session'}
            {(!tooltip.session.end_time || tooltip.session.end_time <= tooltip.session.start_time) && (
              <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 600 }}>⚠ pas de fin</span>
            )}
          </TtTitle>
          <TtRow>
            <Smartphone size={12} />
            {tooltip.username && tooltip.username !== 'Unknown' ? `@${tooltip.username}` : tooltip.deviceName}
          </TtRow>
          <TtRow>
            <Clock size={12} />
            {new Date(tooltip.session.start_time * 1000).toLocaleString('fr-FR', {
              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
            })}
            {tooltip.session.end_time && tooltip.session.end_time > tooltip.session.start_time && (
              <> → {new Date(tooltip.session.end_time * 1000).toLocaleTimeString('fr-FR', {
                hour: '2-digit', minute: '2-digit'
              })}</>
            )}
          </TtRow>
          <TtRow>⏱ <TtValue>{fmtDuration(tooltip.session.duration)}</TtValue></TtRow>
          <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' }}>
            {tooltip.session.likes > 0 && <TtRow><Heart size={10} color="#ec4899" /> <TtValue>{tooltip.session.likes}</TtValue> likes</TtRow>}
            {tooltip.session.follows > 0 && <TtRow><UserPlus size={10} color="#22c55e" /> <TtValue>{tooltip.session.follows}</TtValue> follows</TtRow>}
            {tooltip.session.unfollows > 0 && <TtRow><UserMinus size={10} color="#ef4444" /> <TtValue>{tooltip.session.unfollows}</TtValue> unfollows</TtRow>}
            {tooltip.session.profile_views > 0 && <TtRow><Eye size={10} color="#6366f1" /> <TtValue>{tooltip.session.profile_views}</TtValue> profils</TtRow>}
            {tooltip.session.story_views > 0 && <TtRow><Star size={10} color="#f59e0b" /> <TtValue>{tooltip.session.story_views}</TtValue> stories</TtRow>}
            {tooltip.session.story_likes > 0 && <TtRow><Heart size={10} color="#f59e0b" /> <TtValue>{tooltip.session.story_likes}</TtValue> story likes</TtRow>}
            {tooltip.session.comments > 0 && <TtRow><MessageCircle size={10} color="#06b6d4" /> <TtValue>{tooltip.session.comments}</TtValue> comments</TtRow>}
            {tooltip.session.dms > 0 && <TtRow><MessageCircle size={10} color="#8b5cf6" /> <TtValue>{tooltip.session.dms}</TtValue> DMs</TtRow>}
          </div>
        </Tooltip>
      )}
    </Container>
  );
}
